import { useRouter } from "next/router"
import { type FC, useMemo } from "react"
import { type PageNavigationItemProps } from "@octopus-energy/coral/molecules"
import {
    IconBolt,
    IconFileList,
    IconGift,
    IconHome,
    type IconProps,
    IconWallet,
} from "@octopus-energy/icons"
import { type CookieValueTypes } from "cookies-next"
import { useTranslations } from "next-intl"
import { getQuerylessPath } from "@/lib/routes"
import { dashboardRoutesPaths } from "@/routes/dashboard"
import { useCurrentNavigation } from "./useCurrentNavigation"

type UseDashboardNavigationResult = Array<
    Omit<PageNavigationItemProps, "icon"> & { icon: FC<IconProps>; label: string }
>

export function useDashboardNavigation(lastDashboardNavigationCookie?: CookieValueTypes) {
    const { asPath } = useRouter()
    const t = useTranslations("useDashboardNavigation")
    const { accountNumber, propertyId } = useCurrentNavigation(lastDashboardNavigationCookie)

    return useMemo(() => {
        const navigationItems: UseDashboardNavigationResult = []

        if (!accountNumber) {
            return navigationItems
        }

        if (propertyId) {
            navigationItems.push(
                {
                    href: dashboardRoutesPaths.property.resolve({
                        pathParams: {
                            accountNumber,
                            propertyId,
                        },
                    }),
                    icon: IconHome,
                    label: t("dashboard"),
                    isActive:
                        getQuerylessPath(asPath) ===
                        dashboardRoutesPaths.property.resolve({
                            pathParams: {
                                accountNumber,
                                propertyId,
                            },
                        }),
                },
                {
                    href: dashboardRoutesPaths.billsAndPayments.resolve({
                        pathParams: {
                            accountNumber,
                            propertyId,
                        },
                    }),
                    icon: IconWallet,
                    label: t("bills"),
                    isActive: asPath.includes(
                        dashboardRoutesPaths.billsAndPayments.resolve({
                            pathParams: {
                                accountNumber,
                                propertyId,
                            },
                        })
                    ),
                },
                {
                    href: dashboardRoutesPaths.consumption.resolve({
                        pathParams: {
                            accountNumber,
                            propertyId,
                        },
                    }),
                    icon: IconBolt,
                    label: t("usageGraphs"),
                    isActive: asPath.includes(
                        dashboardRoutesPaths.consumption.resolve({
                            pathParams: {
                                accountNumber,
                                propertyId,
                            },
                        })
                    ),
                },
                {
                    href: dashboardRoutesPaths.agreements.resolve({
                        pathParams: {
                            accountNumber,
                            propertyId,
                        },
                    }),
                    icon: IconFileList,
                    label: t("contracts"),
                    isActive: asPath.includes(
                        dashboardRoutesPaths.agreements.resolve({
                            pathParams: {
                                accountNumber,
                                propertyId,
                            },
                        })
                    ),
                }
            )
        }

        navigationItems.push({
            href: dashboardRoutesPaths.referral.resolve({ pathParams: { accountNumber } }),
            icon: IconGift,
            label: t("referral"),
            isActive: asPath.includes(
                dashboardRoutesPaths.referral.resolve({ pathParams: { accountNumber } })
            ),
        })

        if (navigationItems.length > 0 && !navigationItems.some((item) => item.isActive)) {
            navigationItems[0].isActive = true
        }

        return navigationItems
    }, [asPath, accountNumber, propertyId, t])
}

useDashboardNavigation.messages = ["useDashboardNavigation"] as const
useDashboardNavigation.cookies = [...useCurrentNavigation.cookies] as const
